import { useEffect, useMemo, useState } from 'react';
import { fetchPackageDestinations, fetchPackageOrigins } from '../api/packages.api';
import type { PackagePlace } from '../api/packages.types';

export interface UsePackagePlacesResult {
  places: PackagePlace[];
  isLoading: boolean;
  error: string | null;
}

const EMPTY: PackagePlace[] = [];

function useAsyncPlaces(
  load: ((signal: AbortSignal) => Promise<PackagePlace[]>) | null,
): UsePackagePlacesResult {
  const [places, setPlaces] = useState<PackagePlace[]>(EMPTY);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!load) {
      setPlaces(EMPTY);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setIsLoading(true);
    setError(null);

    load(controller.signal)
      .then((result) => {
        if (!active) return;
        setPlaces(result);
      })
      .catch((cause: unknown) => {
        if (!active || controller.signal.aborted) return;
        setPlaces(EMPTY);
        setError(cause instanceof Error ? cause.message : 'packages.api.requestFailed');
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [load]);

  return { places, isLoading, error };
}

export function usePackageOrigins(): UsePackagePlacesResult {
  return useAsyncPlaces(fetchPackageOrigins);
}

/** Destinations reachable from `originId` (`''` while no origin is selected). */
export function usePackageDestinations(originId: string): UsePackagePlacesResult {
  const load = useMemo(
    () => (originId ? (signal: AbortSignal) => fetchPackageDestinations(originId, signal) : null),
    [originId],
  );

  return useAsyncPlaces(load);
}
